import type { ISO8601DateTime } from 'types/common';
import type { AllPluginSettings } from './plugins.types';

export type Organization = {
  entityId?: number;
  organizationId: number;
  organizationName?: string;
  flowId?: number;
  locations: Location[];
  integrationPrefix?: string;
  monitored: 0 | 1;
  /** Whether the organization needs to complete the setup wizard (set on creation, cleared on completion) */
  setupRequired?: boolean;
  /** Timestamp when the user dismissed the setup wizard without completing */
  setupDismissedAt?: string | null;
};

export type TOrgPredicateState = {
  organizationName: Organization['organizationName'];
  integrationPrefix: Organization['integrationPrefix'];
  monitored: Organization['monitored'];
  clocking: TNormalizedSettings<IntegrationClockingSettings>;
  labor: TNormalizedSettings<IntegrationLaborSettings>;
};

export type Location = {
  locationId: number;
  entityId?: number;
  organizationId: Organization['organizationId'];
  locationName: string;
  locationDescription?: string;
  country?: string;
  timezone?: string;
  shippingAttn?: string;
  shippingAddress?: string;
  shippingCity?: string;
  shippingRegion?: string;
  shippingPostalCode?: string;
  shippingCountry?: string;
};

export type IntegrationSettings<
  TERPType extends IntegrationERP['ERP.selected'] | '' = ''
> = TERPType extends 'InforVisual'
  ? IntegrationERPInforVisual
  : TERPType extends 'Epicor'
  ? IntegrationERPEpicor
  : TERPType extends 'JobBoss'
  ? IntegrationERPJobBoss
  : TERPType extends 'HarmoniErp'
  ? IntegrationERPHarmoni
  : TERPType extends 'Odoo'
  ? IntegrationERPOdoo
  : TERPType extends 'Abas'
  ? IntegrationERPAbas
  : TERPType extends 'InforCSI'
  ? IntegrationERPInforCSI
  : TERPType extends 'GlobalShop'
  ? IntegrationERPGlobalShop
  : TERPType extends 'EvoErp'
  ? IntegrationERPEvoErp
  : TERPType extends 'GreatPlains'
  ? IntegrationERPGreatPlains
  : TERPType extends 'Glovia'
  ? IntegrationERPGlovia
  : TERPType extends 'Syspro'
  ? IntegrationERPSyspro
  : TERPType extends 'ProShop'
  ? IntegrationERPProShop
  : TERPType extends 'Costpoint'
  ? IntegrationERPCostpoint
  : TERPType extends 'MieTrak'
  ? IntegrationERPMieTrak
  : TERPType extends 'Fishbowl'
  ? IntegrationERPFishbowl
  : TERPType extends 'VisualEstiTrack'
  ? IntegrationERPVisualEstiTrack
  : TERPType extends 'Sage500'
  ? IntegrationERPSage500
  : IntegrationSettingsCommon;

export type AllPossibleIntegrationSettings = Partial<
  IntegrationERPInforVisual &
    IntegrationERPEpicor &
    IntegrationERPJobBoss &
    IntegrationERPHarmoni &
    IntegrationERPOdoo &
    IntegrationERPAbas &
    IntegrationERPInforCSI &
    IntegrationERPGlobalShop &
    IntegrationERPEvoErp &
    IntegrationERPGreatPlains &
    IntegrationERPGlovia &
    IntegrationERPSyspro &
    IntegrationERPProShop &
    IntegrationERPCostpoint &
    IntegrationERPMieTrak &
    IntegrationERPFishbowl &
    IntegrationERPVisualEstiTrack &
    IntegrationERPSage500 &
    AllPluginSettings &
    FeatureSettings &
    IntegrationTeepSettings
>;

export type IntegrationSettingsCommon = {
  entityId?: NumberAsString;
  organizationId?: NumberAsString;
  timeZone?: string;
  useCustomIntegrationSettings?: BooleanAsString;
} & Partial<
  IntegrationERP &
    IntegrationClockingSettings &
    IntegrationLaborSettings &
    IntegrationTeepSettings &
    IntegrationAuthSettings &
    FeatureSettings
>;

export type IntegrationERP = {
  'ERP.selected':
    | 'JobBoss'
    | 'Epicor'
    | 'InforVisual'
    | 'HarmoniErp'
    | 'Odoo'
    | 'Abas'
    | 'InforCSI'
    | 'GlobalShop'
    | 'EvoErp'
    | 'GreatPlains'
    | 'Glovia'
    | 'Syspro'
    | 'ProShop'
    | 'Costpoint'
    | 'MieTrak'
    | 'Fishbowl'
    | 'VisualEstiTrack'
    | 'Sage500';
};

/**
 * ERPs that implement integrated checksheets (ERP-sourced defs + sample sync).
 * Keep in sync with integration `ERPS_SUPPORTING_INTEGRATED_CHECKSHEETS`
 * (`integrations/master/integratedChecksheets.ts`).
 */
export const ERPS_SUPPORTING_INTEGRATED_CHECKSHEETS: ReadonlyArray<
  IntegrationERP['ERP.selected']
> = ['ProShop'];

export function erpSupportsIntegratedChecksheets(
  erpSelected: string | null | undefined
): boolean {
  return (
    !!erpSelected &&
    (ERPS_SUPPORTING_INTEGRATED_CHECKSHEETS as ReadonlyArray<string>).includes(
      erpSelected
    )
  );
}

/** An ERP selection timesheets can be enabled for (GET /api/timesheets/erps). */
export type TimesheetErp = { selectedErp: string; version: string };

/** Missing `ERP.selected` means HarmoniErp; a missing version means `default`. */
export function erpSupportsTimesheets(
  timesheetErps: readonly TimesheetErp[],
  erpSelected: string | null | undefined,
  erpVersion: string | null | undefined
): boolean {
  const selectedErp = erpSelected || 'HarmoniErp';
  const version = erpVersion || 'default';
  return timesheetErps.some(
    (erp) => erp.selectedErp === selectedErp && erp.version === version
  );
}

export type IntegrationClockingSettings = {
  // 'clocking.limitOutOfRange': NumberAsString; // for limiting how long in mins an rfid employee tag can be out or range before they dropped off the device - not implemented
  'clocking.manuallyEnterEmployee': BooleanAsString;
  'clocking.minimumLunchTime': NumberAsString; // in mins
  'clocking.verifyClockOut': BooleanAsString;
  'clocking.autoCheckInLunchQuantities': BooleanAsString;
};

export type IntegrationTeepSettings = {
  'teep.schedule.fullPeriod'?: BooleanAsString;
  'teep.availability.ignoreUnscheduled'?: BooleanAsString;
  'teep.availability.collapseSimultaneous'?: BooleanAsString;
};

export type IntegrationLaborSettings = {
  'labor.promptForComments': BooleanAsString;
  'labor.promptForComplete': BooleanAsString;
  'labor.promptForDiscrepantQty': BooleanAsString;
  'labor.promptForGoodQty': BooleanAsString;
  'labor.promptForScrapQty': BooleanAsString;
  'labor.promptForRework': BooleanAsString;
  'labor.promptForStartSetup': BooleanAsString;
  'labor.showStartIndirectTab': BooleanAsString;
  'labor.showPauseLaborTimeButton': BooleanAsString;
  'labor.pauseLaborTimeIndirectCode': string;
  'labor.earnedHrsPartial': BooleanAsString;
};

export type IntegrationAuthSettings = {
  auth: string;
};

export type FeatureSettings = {
  'feature.harmoniAi.enabled'?: BooleanAsString;
  /**
   * When true (and ERP.selected is in ERPS_SUPPORTING_INTEGRATED_CHECKSHEETS),
   * device checksheets load defs from the ERP instead of MediaWiki and sync
   * samples bidirectionally where supported.
   */
  'feature.checksheets.integrated'?: BooleanAsString;
  'feature.activity.scrapPredictor.enabled'?: BooleanAsString;
  'feature.timesheets.enabled'?: BooleanAsString;
  'efficiency.thresholdLow'?: string;
  'efficiency.thresholdHigh'?: string;
};

export type IntegrationERPInforVisual = IntegrationSettingsCommon & {
  'ERP.InforVisual.siteId'?: string;
  'ERP.InforVisual.version': 'default';
  'ERP.InforVisual.subVersion': never;
  'ERP.InforVisual.password'?: string;
  'ERP.InforVisual.userName'?: string;
  'ERP.InforVisual.clientPath'?: string;
  'ERP.InforVisual.visualInstance'?: string;
  'ERP.InforVisual.breakIndirectCode'?: string;
  'ERP.InforVisual.clockedInIndirectId'?: string;
  'ERP.InforVisual.lunchIndirectCode'?: string;
  'ERP.InforVisual.meetingIndirectCode'?: string;
  'ERP.InforVisual.lunchPaid'?: BooleanAsString;
  'ERP.InforVisual.trackBreaks'?: BooleanAsString;
  'ERP.InforVisual.trackLunch'?: BooleanAsString;
  'ERP.InforVisual.trackMeetings'?: BooleanAsString;
  'ERP.InforVisual.SQLServer.password'?: string;
  'ERP.InforVisual.SQLServer.userName'?: string;
  'ERP.InforVisual.SQLServer.databaseName'?: string;
  'ERP.InforVisual.SQLServer.databaseServer'?: string;
  'ERP.InforVisual.SQLServer.authenticationType'?: string;
};

/**
 * SQLServer settings only apply if Epicor version is 'progress'
 */
export type IntegrationERPEpicor = IntegrationSettingsCommon & {
  'ERP.Epicor.apiKey'?: string;
  'ERP.Epicor.appServerUrl'?: string;
  'ERP.Epicor.breaksPaid'?: BooleanAsString;
  'ERP.Epicor.companyId'?: string;
  'ERP.Epicor.connectionType'?: string;
  'ERP.Epicor.breakIndirectCode'?: string;
  'ERP.Epicor.lunchIndirectCode'?: string;
  'ERP.Epicor.lunchPaid'?: BooleanAsString;
  'ERP.Epicor.useStandardLunchDeductions'?: BooleanAsString;
  'ERP.Epicor.meetingIndirectCode'?: string;
  'ERP.Epicor.password'?: string;
  'ERP.Epicor.plantId'?: string;
  'ERP.Epicor.trackBreaks'?: BooleanAsString;
  'ERP.Epicor.trackLunch'?: BooleanAsString;
  'ERP.Epicor.trackMeetings'?: BooleanAsString;
  'ERP.Epicor.userName'?: string;
  'ERP.Epicor.version': 'kinetic' | 'progress';
  'ERP.Epicor.subVersion': 'latest' | '<10.2.600' | '10.2.600+' | '<11.2';
  'ERP.Epicor.SQLServer.password'?: string;
  'ERP.Epicor.SQLServer.userName'?: string;
  'ERP.Epicor.SQLServer.databaseName'?: string;
  'ERP.Epicor.SQLServer.databaseServer'?: string;
  'ERP.Epicor.SQLServer.authenticationType'?: string;
};

export type IntegrationERPJobBoss = IntegrationSettingsCommon & {
  'ERP.JobBoss.userName'?: string;
  'ERP.JobBoss.password'?: string;
  'ERP.JobBoss.version': 'classic' | 'e2' | 'cloud';
  'ERP.JobBoss.subVersion': never;
  'ERP.JobBoss.clientId': string;
  'ERP.JobBoss.clientSecret': string;
  // JobBOSS² Cloud-only: API + OAuth host pair. The Connectivity tab writes
  // these as a matched set keyed off a Public Cloud / GovCloud environment
  // selector; backend defaults to Public Cloud when blank.
  'ERP.JobBoss.apiUrl'?: string;
  'ERP.JobBoss.authUrl'?: string;
  // Attendance code used when Harmoni creates a new attendance-ticket-detail
  // on clock-in (JobBOSS² Cloud).
  'ERP.JobBoss.defaultAttendanceCode'?: string;
  'ERP.JobBoss.SQLServer.authenticationType'?: string;
  'ERP.JobBoss.SQLServer.databaseName'?: string;
  'ERP.JobBoss.SQLServer.databaseServer'?: string;
  'ERP.JobBoss.SQLServer.password'?: string;
  'ERP.JobBoss.SQLServer.userName'?: string;
  // JobBOSS² Cloud overtime calculation. Read by the integration backend at
  // clock-in / clock-out to flag attendance-ticket-details with isOvertime.
  'ERP.JobBoss.overtime.enabled'?: BooleanAsString;
  'ERP.JobBoss.overtime.period'?: 'week' | 'day';
  'ERP.JobBoss.overtime.thresholdHrs'?: string;
  'ERP.JobBoss.overtime.weekStartDay'?:
    | 'Sunday'
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday';
  // JobBOSS² Cloud auto lunch deduction. Shift windows take precedence at
  // clock-out; elapsed hours remain the fallback for unconfigured shifts.
  'ERP.JobBoss.lunchDeduction.enabled'?: BooleanAsString;
  'ERP.JobBoss.lunchDeduction.afterHours'?: string;
  'ERP.JobBoss.lunchDeduction.deductMinutes'?: string;
  'ERP.JobBoss.lunchDeduction.shiftWindows'?: string;
  // JobBoss Classic: when false, scrap qty/reason are Harmoni-only (not written to ERP).
  'ERP.JobBoss.scrap.writeToErp'?: BooleanAsString;
};

export type IntegrationERPHarmoni = IntegrationSettingsCommon & {
  'ERP.HarmoniErp.version': 'default';
  'ERP.HarmoniErp.subVersion': never;
  'ERP.HarmoniErp.breakIndirectCode'?: string;
  'ERP.HarmoniErp.lunchIndirectCode'?: string;
};

export type IntegrationERPOdoo = IntegrationSettingsCommon & {
  'ERP.Odoo.userName'?: string;
  'ERP.Odoo.password'?: string;
  'ERP.Odoo.version': 'default';
  'ERP.Odoo.subVersion': never;
  'ERP.Odoo.appServerUrl'?: string;
  'ERP.Odoo.connectionType'?: 'XML-RPC';
  'ERP.Odoo.apiKey'?: string;
  'ERP.Odoo.dbName'?: string;
};

export type IntegrationERPAbas = IntegrationSettingsCommon & {
  'ERP.Abas.version': 'rest';
  'ERP.Abas.subVersion': never;
  'ERP.Abas.userName'?: string;
  'ERP.Abas.password'?: string;
  'ERP.Abas.baseUrl'?: string;
  'ERP.Abas.clientId'?: string;
};

export type IntegrationERPInforCSI = IntegrationSettingsCommon & {
  'ERP.InforCSI.configGroup'?: string;
  'ERP.InforCSI.version': 'default';
  'ERP.InforCSI.subVersion': never;
  'ERP.InforCSI.password'?: string;
  'ERP.InforCSI.userName'?: string;
  'ERP.InforCSI.baseUrl'?: string;
  'ERP.InforCSI.SQLServer.password'?: string;
  'ERP.InforCSI.SQLServer.userName'?: string;
  'ERP.InforCSI.SQLServer.databaseName'?: string;
  'ERP.InforCSI.SQLServer.databaseServer'?: string;
  'ERP.InforCSI.SQLServer.authenticationType'?: string;
};

export type IntegrationERPGlobalShop = IntegrationSettingsCommon & {
  'ERP.GlobalShop.version': 'default';
  'ERP.GlobalShop.subVersion': never;
  'ERP.GlobalShop.Odbc.dsn'?: string;
  'ERP.GlobalShop.Odbc.userName'?: string;
  'ERP.GlobalShop.Odbc.password'?: string;
  'ERP.GlobalShop.companyCode'?: string;
  'ERP.GlobalShop.breakIndirectCode'?: string;
  'ERP.GlobalShop.clockedInIndirectId'?: string;
  'ERP.GlobalShop.lunchIndirectCode'?: string;
  'ERP.GlobalShop.meetingIndirectCode'?: string;
  'ERP.GlobalShop.lunchPaid'?: BooleanAsString;
  'ERP.GlobalShop.trackBreaks'?: BooleanAsString;
  'ERP.GlobalShop.trackLunch'?: BooleanAsString;
  'ERP.GlobalShop.trackMeetings'?: BooleanAsString;
};

export type IntegrationERPEvoErp = IntegrationSettingsCommon & {
  'ERP.EvoErp.version': 'default';
  'ERP.EvoErp.subVersion': never;
  'ERP.EvoErp.Odbc.dsn'?: string;
  'ERP.EvoErp.Odbc.userName'?: string;
  'ERP.EvoErp.Odbc.password'?: string;
  'ERP.EvoErp.breakIndirectCode'?: string;
  'ERP.EvoErp.clockedInIndirectId'?: string;
  'ERP.EvoErp.lunchIndirectCode'?: string;
  'ERP.EvoErp.meetingIndirectCode'?: string;
  'ERP.EvoErp.lunchPaid'?: BooleanAsString;
  'ERP.EvoErp.trackBreaks'?: BooleanAsString;
  'ERP.EvoErp.trackLunch'?: BooleanAsString;
  'ERP.EvoErp.trackMeetings'?: BooleanAsString;
};

export type IntegrationERPGreatPlains = IntegrationSettingsCommon & {
  'ERP.GreatPlains.version': 'default';
  'ERP.GreatPlains.subVersion': never;
  'ERP.GreatPlains.SQLServer.authenticationType'?: string;
  'ERP.GreatPlains.SQLServer.databaseServer'?: string;
  'ERP.GreatPlains.SQLServer.databaseName'?: string;
  'ERP.GreatPlains.SQLServer.userName'?: string;
  'ERP.GreatPlains.SQLServer.password'?: string;
};

export type IntegrationERPGlovia = IntegrationSettingsCommon & {
  'ERP.Glovia.version': 'default';
  'ERP.Glovia.subVersion': never;
  /**
   * Oracle schema that owns the Glovia tables (e.g. `GLOVIA_PROD`).
   * Used to fully qualify references like `GLOVIA_PROD.WC` at query time.
   */
  'ERP.Glovia.schemaPrefix'?: string;
  /**
   * Glovia company / tenant identifier (`CCN` column — e.g. `RPC`).
   * When set, every Glovia read is automatically scoped to this
   * company via `WHERE CCN = '<companyID>'`.
   */
  'ERP.Glovia.companyID'?: string;
  'ERP.Glovia.Odbc.dsn'?: string;
  'ERP.Glovia.Odbc.userName'?: string;
  'ERP.Glovia.Odbc.password'?: string;
};

export type IntegrationERPSyspro = IntegrationSettingsCommon & {
  'ERP.Syspro.version': 'default';
  'ERP.Syspro.subVersion': never;
  /**
   * SYSPRO company code. Reserved for scoping reads to a single company
   * when the SYSPRO database is shared across companies.
   */
  'ERP.Syspro.companyID'?: string;
  // SQL Server connection consumed by the Connector Client's SYSPRO plugin.
  'ERP.Syspro.SQLServer.authenticationType'?: string;
  'ERP.Syspro.SQLServer.databaseServer'?: string;
  'ERP.Syspro.SQLServer.databaseName'?: string;
  'ERP.Syspro.SQLServer.userName'?: string;
  'ERP.Syspro.SQLServer.password'?: string;
  // e.net Communications Load Balancer used for WIPTLP labor write-back.
  /** e.g. net.tcp://server:31001/SYSPROWCFService; blank disables write-back. */
  'ERP.Syspro.Enet.baseUrl'?: string;
  /** SYSPRO operator used for e.net logon. */
  'ERP.Syspro.Enet.userName'?: string;
  'ERP.Syspro.Enet.password'?: string;
  'ERP.Syspro.Enet.companyId'?: string;
  'ERP.Syspro.Enet.companyPassword'?: string;
  /** SYSPRO/Encore instance for Logon (default 0). */
  'ERP.Syspro.Enet.instance'?: string;
  /** 'true' posts WIPTLP with ValidateOnly=Y (pilot mode, writes nothing). */
  'ERP.Syspro.Enet.validateOnly'?: BooleanAsString;
  /** WIPTLP work centre rate indicator (default 1). */
  'ERP.Syspro.Enet.wcRateInd'?: string;
  /** WIPTLP employee rate indicator (default 1). */
  'ERP.Syspro.Enet.employeeRateInd'?: string;
};

export type IntegrationERPProShop = IntegrationSettingsCommon & {
  'ERP.ProShop.version': 'default';
  'ERP.ProShop.subVersion': never;
  /** Tenant base URL, e.g. https://yourcompany.adionsystems.com */
  'ERP.ProShop.baseUrl'?: string;
  'ERP.ProShop.userName'?: string;
  'ERP.ProShop.password'?: string;
};

export type IntegrationERPCostpoint = IntegrationSettingsCommon & {
  'ERP.Costpoint.version': 'default';
  'ERP.Costpoint.subVersion': never;
  /** Costpoint REST facade URL, e.g. https://xxxx.execute-api.us-gov-east-1.amazonaws.com */
  'ERP.Costpoint.baseUrl'?: string;
  'ERP.Costpoint.tenantId'?: string;
  'ERP.Costpoint.companyId'?: string;
  'ERP.Costpoint.userName'?: string;
  /** Long-lived Costpoint JWT sent as the Authorization Bearer token. */
  'ERP.Costpoint.password'?: string;
  /** Comma-separated work center IDs treated as quality/inspection ops. */
  'ERP.Costpoint.qualityResourceIDs'?: string;
  /** Timesheet hours post on labor end unless set to 'false'. */
  'ERP.Costpoint.timesheetPosting'?: string;
};

export type IntegrationERPMieTrak = IntegrationSettingsCommon & {
  'ERP.MieTrak.version': 'default';
  'ERP.MieTrak.subVersion': never;
  /** SQL Server connection consumed by the Connector Client Database.ExecuteQuery path. */
  'ERP.MieTrak.SQLServer.authenticationType'?: string;
  'ERP.MieTrak.SQLServer.databaseServer'?: string;
  'ERP.MieTrak.SQLServer.databaseName'?: string;
  'ERP.MieTrak.SQLServer.userName'?: string;
  'ERP.MieTrak.SQLServer.password'?: string;
  'ERP.MieTrak.SQLServer.port'?: string;
  /** SQL Server connection for labor/clock staging writes (Database.ExecuteNonQuery). */
  'ERP.MieTrak.WriteSQLServer.authenticationType'?: string;
  'ERP.MieTrak.WriteSQLServer.databaseServer'?: string;
  'ERP.MieTrak.WriteSQLServer.databaseName'?: string;
  'ERP.MieTrak.WriteSQLServer.userName'?: string;
  'ERP.MieTrak.WriteSQLServer.password'?: string;
  'ERP.MieTrak.WriteSQLServer.port'?: string;
};

export type IntegrationERPFishbowl = IntegrationSettingsCommon & {
  /** `odbc` = MySQL via Connector Client ODBC; future `api` = Fishbowl REST. */
  'ERP.Fishbowl.version': 'odbc';
  'ERP.Fishbowl.subVersion': never;
  /** ODBC DSN for the Fishbowl MySQL database on the Connector Client machine. */
  'ERP.Fishbowl.Odbc.dsn'?: string;
  'ERP.Fishbowl.Odbc.userName'?: string;
  'ERP.Fishbowl.Odbc.password'?: string;
  /**
   * Separate ODBC connection for laborRecs / clockingRecs, which live in a
   * customer-managed schema rather than in Fishbowl. Needs INSERT and UPDATE;
   * falls back to the Fishbowl connection above when unset.
   */
  'ERP.Fishbowl.WriteOdbc.dsn'?: string;
  'ERP.Fishbowl.WriteOdbc.userName'?: string;
  'ERP.Fishbowl.WriteOdbc.password'?: string;
  /** Schema holding `laborrecs` / `clockingrecs`. Defaults to `harmoni`. */
  'ERP.Fishbowl.LaborDb.schema'?: string;
};

export type IntegrationERPVisualEstiTrack = IntegrationSettingsCommon & {
  'ERP.VisualEstiTrack.version': 'odbc';
  'ERP.VisualEstiTrack.subVersion': never;
  /** 32-bit Visual FoxPro ODBC DSN on the Connector Client machine, pointing at the VET `.DBC`. */
  'ERP.VisualEstiTrack.Odbc.dsn'?: string;
  'ERP.VisualEstiTrack.Odbc.userName'?: string;
  'ERP.VisualEstiTrack.Odbc.password'?: string;
};

export type IntegrationERPSage500 = IntegrationSettingsCommon & {
  'ERP.Sage500.version': 'default';
  'ERP.Sage500.subVersion': never;
  /** Sage 500 company (tsmCompany.CompanyID) that reads and labor posts are scoped to. */
  'ERP.Sage500.companyID'?: string;
  /** SQL Server connection used for reads and E2B labor posts; the login must be a Sage user. */
  'ERP.Sage500.SQLServer.authenticationType'?: string;
  'ERP.Sage500.SQLServer.databaseServer'?: string;
  'ERP.Sage500.SQLServer.databaseName'?: string;
  'ERP.Sage500.SQLServer.userName'?: string;
  'ERP.Sage500.SQLServer.password'?: string;
  'ERP.Sage500.SQLServer.port'?: string;
};

export type NumberAsString = `${number}`;
export type BooleanAsString = 'true' | 'false';

export type OrganizationsState = {
  organizations: {
    values: Organization[];
    lastUpdated?: ISO8601DateTime;
  };
  organizationSettingsMapByOrgId: Record<
    Organization['organizationId'],
    AllPossibleIntegrationSettings
  > | null;
};

/**
 * Helper type for working with Integration Settings.
 * Converts all string boolean values to boolean.
 */
export type TStringBoolToBool<
  T extends Record<string, string | number | boolean>
> = {
  [K in keyof T]: T[K] extends BooleanAsString ? boolean : T[K];
};

/**
 * Helper type for working with Integration Settings.
 * Converts all string number values to number.
 */
export type TStringNumberToNumber<
  T extends Record<string, string | number | boolean>
> = {
  [K in keyof T]: T[K] extends NumberAsString ? number : T[K];
};

/**
 * Helper type for working with Integration Settings.
 * Provides all possible ERP prefixes.
 */
export type TSettingsERPPrefix = `ERP.${IntegrationERP['ERP.selected']}`;

/**
 * Helper type for working with Integration Settings.
 * Removes the prefix from the keys.
 */
export type TStripPrefix<T extends Record<string, any>> = {
  [K in keyof T as K extends
    | `${string}.${string}.${string}.${infer U}`
    | `${string}.${string}.${infer U}`
    | `${string}.${infer U}`
    ? U
    : K]: T[K];
};

/**
 * Helper type for working with Integration Settings.
 * Normalizes the settings by removing the prefix and converting
 * string boolean values to boolean, and string number values to number.
 */
export type TNormalizedSettings<TSettings extends Record<string, string>> =
  TStripPrefix<TStringNumberToNumber<TStringBoolToBool<TSettings>>>;
